import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    input,
    Output,
    signal,
    viewChild,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@pure/hive';
import { faArrowRight, faSquare } from '@fortawesome/pro-solid-svg-icons';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, share, switchMap } from 'rxjs/operators';
import { PromptCatalogService, PromptQuestion } from '@pure1/data';
import { trackQuestionClicked } from '../copilot-question-analytics';

/** What the input invites when the conversation is in its ordinary, unpaused state. */
export const DEFAULT_INPUT_PLACEHOLDER = 'Ask me anything...';

/** Under this many characters the text is too unspecific to search the catalog for. */
const SUGGESTION_MIN_CHARS = 2;
/** The most catalog questions the dropdown shows. */
const SUGGESTION_LIMIT = 8;
/**
 * How long the user has to pause typing before a lookup is made. One timer paces both the
 * catalog search and the model completion — renamed from SUGGESTION_DEBOUNCE_MS when the
 * second joined it (CLOUD-261103).
 */
const INPUT_DEBOUNCE_MS = 250;

/**
 * The server will not complete a prefix shorter than this, so asking about one buys an empty
 * answer at the price of a request. Mirrors `min_prefix_words` in backchatty's
 * `completion_config.py` — if that moves, this should follow.
 */
const MIN_COMPLETION_WORDS = 3;

@Component({
    selector: 'copilot-input-bar',
    standalone: true,
    imports: [FormsModule, ButtonComponent],
    templateUrl: './input-bar.component.html',
    styleUrl: './input-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputBarComponent implements AfterViewInit {
    @Input() isGenerating: boolean = false;
    @Input() isLandingAskBar: boolean = false;
    /**
     * Prompt text for the input. Overridden while a triage clarification is outstanding,
     * because there the input's offered role is answering the assistant's question rather
     * than starting something new.
     *
     * `isGenerating` still wins: while a response is streaming the user cannot type at all.
     */
    readonly placeholder = input<string>(DEFAULT_INPUT_PLACEHOLDER);
    /**
     * Whether this surface offers prompt-catalog suggestions while the user types.
     *
     * Opt-in, because the three chat boxes share this component and only two of them want it:
     * the landing ask bar and the side panel. The conversation footer is deliberately left out —
     * mid-conversation, canned catalog questions are noise rather than help — and any future
     * surface starts out the same way rather than inheriting a feature nobody asked it to have.
     */
    readonly suggestionsEnabled = input(false);
    /**
     * The conversation this chat box is typing into, forwarded with each completion request so
     * the server can ground the suffix in that conversation rather than treating every prefix
     * as a cold start.
     *
     * Empty on the landing screen, which has no conversation yet — the proto asks for an empty
     * string there rather than an omitted field.
     */
    readonly sessionId = input('');
    @Output() inputSubmit = new EventEmitter<string>();
    @Output() stop = new EventEmitter<void>();

    @ViewChild('inputBar') readonly inputBarElem!: ElementRef<HTMLInputElement>;
    /** A signal query, because the overlay only exists while a completion is showing. */
    private readonly ghostOverlayElem = viewChild<ElementRef<HTMLElement>>('ghostOverlay');

    inputText = '';

    /** Each instance owns its listbox id: the side panel and the page can both hold an input bar. */
    private static nextListboxId = 0;
    protected readonly listboxId = `copilot-suggestions-${InputBarComponent.nextListboxId++}`;

    /** Catalog questions matching the current text. Empty means the dropdown is closed. */
    protected readonly suggestions = signal<PromptQuestion[]>([]);
    /** The keyboard-highlighted suggestion, or -1 when none is. */
    protected readonly activeIndex = signal(-1);

    /**
     * The suffix the server offered for what is currently typed, drawn as ghost text after it
     * and accepted with Tab. Empty means nothing is offered, which is the ordinary state: the
     * server answers with an empty completion whenever it is not confident.
     *
     * Unlike the dropdown this is not opt-in per surface. An inline suffix is not the
     * interruption a dropdown is, so every chat box gets it.
     */
    protected readonly completion = signal('');

    private readonly query$ = new Subject<string>();

    constructor(private readonly promptCatalog: PromptCatalogService) {
        // One debounce, two lookups. They stay separate calls on the same settled text because
        // they answer at different speeds: search comes from an index and the dropdown paints at
        // its latency, while completion waits on a model. Merging them would make the dropdown
        // wait on the model, which is the trade the proto explicitly declines.
        const settled$ = this.query$.pipe(debounceTime(INPUT_DEBOUNCE_MS), share());

        settled$
            .pipe(
                map(text => text.trim()),
                // switchMap drops the in-flight lookup for superseded text, so a slow response for
                // "aler" can never land on top of the results for "alerts". Text under the floor
                // maps to an empty list rather than being filtered out: that is what closes the
                // dropdown when the user deletes back down to one character.
                switchMap(text =>
                    !this.suggestionsEnabled() || text.length < SUGGESTION_MIN_CHARS
                        ? of([] as PromptQuestion[])
                        : this.promptCatalog.searchPrompts(text, SUGGESTION_LIMIT).pipe(
                              // A failed lookup is an absent dropdown, never a broken chat box —
                              // but log it, or a broken endpoint is indistinguishable from a
                              // query that genuinely matches nothing.
                              catchError(err => {
                                  console.error('Failed to search the Copilot prompt catalog', err);
                                  return of([] as PromptQuestion[]);
                              }),
                              // Only answers for what is in the box right now may be shown. The
                              // user can outrun a lookup — sending or clearing the text while it
                              // is in flight — and its late answer must not reopen the dropdown
                              // over a box that has moved on. The under-the-floor branch above is
                              // deliberately not filtered: emitting its empty list is what closes
                              // the dropdown.
                              filter(() => text === this.inputText.trim()),
                          ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(results => {
                this.suggestions.set(results);
                this.activeIndex.set(-1);
            });

        settled$
            .pipe(
                // Sent verbatim rather than trimmed: the proto's contract is that the suffix
                // extends the request text exactly, so a space the user has already typed is
                // part of what the completion has to follow.
                switchMap(text =>
                    this.shouldComplete(text)
                        ? this.promptCatalog.completePrompt(text, this.sessionId())
                        : of({ completion: '', prefix: text }),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(({ completion, prefix }) => {
                // Only an answer for what is in the box right now may be drawn. Completion is
                // slow next to typing, so a late answer for superseded text is routine.
                if (prefix === this.inputText) {
                    this.completion.set(completion);
                }
            });
    }

    ngAfterViewInit(): void {
        // Currently, whenever the component is created, we want to have the input be focused
        this.focus();
    }

    isSubmitDisabled(): boolean {
        return this.inputText.length === 0 || this.inputText.trim().length === 0;
    }

    clearInput(): void {
        this.inputText = '';
        this.closeSuggestions();
        this.completion.set('');
    }

    /**
     * Enter confirms an IME candidate, picks a highlighted suggestion, or sends the message, and
     * the three have to be told apart in that order. Only keydown can do it: it is dispatched while
     * the composition is still live, and by the time the matching keyup arrives the browser has
     * already ended the composition and reports `isComposing === false` — which is why this cannot
     * hang off keyup.
     */
    protected onEnterKeydown(event: Event): void {
        if (event instanceof KeyboardEvent && event.isComposing) {
            return;
        }

        const highlighted = this.showSuggestions() ? this.suggestions()[this.activeIndex()] : undefined;
        if (highlighted) {
            this.selectSuggestion(highlighted);
            return;
        }

        this.clickSubmit();
    }

    /**
     * Escape takes down both assists at once — the dropdown and the ghost — and nothing else.
     *
     * The side panel closes itself on Escape through its own host listener, so while either is
     * up the event must stop here; otherwise one Escape would take the panel down with them.
     * With neither showing there is nothing to dismiss, so the event is left alone and the
     * panel still closes.
     */
    protected onEscapeKeydown(event: Event): void {
        if (this.showSuggestions() || this.completion()) {
            event.stopPropagation();
        }

        this.closeSuggestions();
        this.completion.set('');
    }

    /**
     * Tab takes the offered suffix — but only when one is showing. With nothing to accept it
     * has to go on doing what Tab does everywhere else, so the default is suppressed no more
     * often than the key is actually being used for something.
     *
     * A highlighted suggestion does not change this: Enter picks from the dropdown, Tab takes
     * the ghost, and each key keeps one job.
     */
    protected onTabKeydown(event: Event): void {
        if (!this.completion()) {
            return;
        }

        event.preventDefault();
        this.acceptCompletion();
    }

    /**
     * The overlay is its own scroll box, so once the text outruns the bar the two have to be
     * kept in step or the ghost drifts away from the words it is meant to follow.
     */
    protected syncGhostScroll(): void {
        const overlay = this.ghostOverlayElem()?.nativeElement;
        if (overlay) {
            overlay.scrollLeft = this.inputBarElem.nativeElement.scrollLeft;
        }
    }

    /** Both assists are hints about the text being typed; neither outlives the focus. */
    protected onBlur(): void {
        this.closeSuggestions();
        this.completion.set('');
    }

    protected showSuggestions(): boolean {
        return this.suggestionsEnabled() && !this.isGenerating && this.suggestions().length > 0;
    }

    protected optionId(index: number): string {
        return `${this.listboxId}-option-${index}`;
    }

    protected activeOptionId(): string | null {
        return this.showSuggestions() && this.activeIndex() >= 0 ? this.optionId(this.activeIndex()) : null;
    }

    protected onInputChange(text: string): void {
        // A suffix computed for older text is the wrong text. Drop it on the keystroke rather
        // than waiting for the next answer, which may be a second away or may never come.
        this.completion.set('');
        // Pushed whatever the surface has opted into: the completion branch listens on every
        // surface, and the search branch checks `suggestionsEnabled` for itself.
        this.query$.next(text ?? '');
    }

    protected moveHighlight(delta: 1 | -1, event: Event): void {
        if (!this.showSuggestions()) {
            return;
        }
        // Otherwise the arrow also throws the caret to the start or end of the text.
        event.preventDefault();
        const count = this.suggestions().length;
        this.activeIndex.update(current =>
            current < 0 ? (delta > 0 ? 0 : count - 1) : (current + delta + count) % count,
        );
    }

    /** Fills the input with the question — the user still has to send it themselves. */
    protected selectSuggestion(question: PromptQuestion): void {
        this.inputText = question.text;
        // ngModel writes the model to the DOM asynchronously; the caret has to be placed against
        // the new value now, so write it directly as well.
        const input = this.inputBarElem.nativeElement;
        input.value = question.text;
        input.setSelectionRange(question.text.length, question.text.length);
        this.closeSuggestions();
        this.completion.set('');
        this.focus();
        trackQuestionClicked(question.text, 'typeahead');
    }

    protected closeSuggestions(): void {
        this.suggestions.set([]);
        this.activeIndex.set(-1);
        // Stand down anything still in the pipe. An empty query supersedes a debounce that has
        // not fired yet — so a lookup the user outran is never even issued — and cancels an
        // in-flight request through switchMap.
        this.query$.next('');
    }

    clickSubmit(): void {
        if (this.isSubmitDisabled()) {
            return;
        }

        this.inputSubmit.emit(this.inputText.trim());
        this.clearInput();
        this.focus();
    }

    clickStop(): void {
        this.stop.emit();
        this.focus();
    }

    focus(): void {
        this.inputBarElem?.nativeElement?.focus();
    }

    private shouldComplete(text: string): boolean {
        if (this.isGenerating) {
            return false;
        }

        const trimmed = text.trim();
        return trimmed.length > 0 && trimmed.split(/\s+/).length >= MIN_COMPLETION_WORDS;
    }

    private acceptCompletion(): void {
        this.inputText += this.completion();
        this.completion.set('');
        // The list was answering the shorter text, so it is stale the moment the suffix lands.
        // Leaving it up also leaves its highlight, and the next Enter would pick that instead
        // of sending what was just accepted.
        this.closeSuggestions();

        // ngModel writes the model to the DOM on the next change detection, and the browser
        // leaves the caret where it was — mid-string, before the text just accepted. Writing
        // the value here lets the caret be placed after it straight away; the later ngModel
        // write of the same string is a no-op. Same reasoning as selectSuggestion above.
        const input = this.inputBarElem.nativeElement;
        input.value = this.inputText;
        input.setSelectionRange(this.inputText.length, this.inputText.length);
    }

    protected readonly faArrowRight = faArrowRight;
    protected readonly faSquare = faSquare;
}
